import type {ApiException} from "../ApiException";
import {StatusCodes} from "../Enums/StatusCodes";
import ErrorMessage from "./ErrorMessage";
import type IErrorContract from "./IErrorContract";


export default class ErrorHandler {
    private readonly mLocalization: Localization;

    public constructor(
        localization: Localization,
    ) {
        this.mLocalization = localization;
    }

    public getErrorText(ajax: JQuery.jqXHR, defaultMessage?: string | null, ignoreErrorDetails?: boolean): string[] {
        const response = ajax.responseText;
        const statusCode = ajax.status;

        return this.getErrorTextImpl(response, statusCode, defaultMessage, ignoreErrorDetails);
    }

    /***
     * Returns promise with error. Never rejects and falls back.
     * @param ajax
     * @param defaultMessage
     * @param ignoreErrorDetails
     */
    public getXHRErrorText(ajax: XMLHttpRequest, defaultMessage?: string | null, ignoreErrorDetails?: boolean): Promise<string[]> {
        const responsePromise = new Promise<string>((resolve, reject) => {
            if (ajax.responseType === "text" || ajax.responseType === "") {
                resolve(ajax.responseText);
            }
            if (ajax.responseType === "blob") {
                const blobResponse = (ajax.response as Blob);
                if (blobResponse.type === "text/plain" || blobResponse.type === "application/json") {
                    const textPromise = blobResponse.text();
                    textPromise.then((result) => resolve(result), reject);
                }
            }
            reject();
        });

        const statusCode = ajax.status;

        return new Promise<string[]>(((resolve, _) => {
            responsePromise.then((responsePromiseResult) => {
                resolve(this.getErrorTextImpl(responsePromiseResult, statusCode, defaultMessage, ignoreErrorDetails));
            }, () => {
                resolve(this.getErrorTextImpl("", statusCode, defaultMessage, ignoreErrorDetails));
            });
        }));
    }

    public getExceptionErrorText(exception: ApiException, defaultMessage?: string | null, ignoreErrorDetails?: boolean): string[] {
        const response = exception?.response;
        const statusCode = exception?.status;

        return this.getErrorTextImpl(response, statusCode, defaultMessage, ignoreErrorDetails);
    }

    private getErrorTextImpl(response: string, statusCode: number, defaultMessage?: string | null, ignoreErrorDetails?: boolean): string[] {
        try {
            const parsedResponse = JSON.parse(response);

            if (this.isErrorContract(parsedResponse)) {
                const messages = parsedResponse.errorDescriptions.map((error) => error.text);
                return messages;
            } else { // valid json, but unexpected object
                return this.getErrorByCodeOrDefault(statusCode, defaultMessage, ignoreErrorDetails);
            }
        } catch (e) { // response is not a valid json, show generic error
            return this.getErrorByCodeOrDefault(statusCode, defaultMessage, ignoreErrorDetails);
        }
    }

    public tryGetExceptionErrorDetail(exception: ApiException): string[] | null {
        const response = exception.response;

        try {
            const parsedResponse = JSON.parse(response) as IErrorContract;

            if (this.isErrorContract(parsedResponse)) {
                return parsedResponse.detailTechnicalErrorDescriptions?.map((error) => error.text) ?? null;
            } else { // valid json, but unexpected object
                return null;
            }
        } catch (e) { // response is not a valid json, show generic error
            return null;
        }
    }

    public getErrorTextAsync(ajax: JQuery.jqXHR, defaultMessage?: string | null): Promise<ErrorMessage> {
        const response = ajax.responseText;
        const statusCode = ajax.status;

        return this.getErrorTextAsyncImpl(response, statusCode, defaultMessage);
    }

    public getXHRErrorTextAsync(ajax: XMLHttpRequest, defaultMessage?: string | null): Promise<ErrorMessage> {
        const response = ajax.responseText;
        const statusCode = ajax.status;

        return this.getErrorTextAsyncImpl(response, statusCode, defaultMessage);
    }

    private getErrorTextAsyncImpl(response: string, statusCode: number, defaultMessage?: string | null): Promise<ErrorMessage> {
        return new Promise((resolve, reject) => {
            try {
                const parsedResponse = JSON.parse(response);

                if (this.isErrorContract(parsedResponse)) {
                    const messages = parsedResponse.errorDescriptions.map((error) => error.text);
                    const detailTechnicalErrorDescriptions = parsedResponse.detailTechnicalErrorDescriptions?.map((error) => error.text) ?? null;

                    resolve(new ErrorMessage(
                        null,
                        null,
                        messages,
                        detailTechnicalErrorDescriptions,
                        parsedResponse.correlationId,
                    ));
                } else { // valid json, but unexpected object
                    this.getErrorByCodeOrDefaultAsync(statusCode, defaultMessage)
                        .then((errorMessageResult) => {
                            resolve(errorMessageResult);
                        }, (errorMessageResult) => {
                            reject(errorMessageResult);
                        });
                }
            } catch (e) { // response is not a valid json, show generic error
                this.getErrorByCodeOrDefaultAsync(statusCode, defaultMessage)
                    .then((errorMessageResult) => {
                        resolve(errorMessageResult);
                    }, (errorMessageResult) => {
                        reject(errorMessageResult);
                    });
            }
        });
    }

    private getErrorByCodeOrDefault(statusCode: number, defaultMessage?: string | null, ignoreErrorDetails?: boolean): string[] {
        if (defaultMessage) {
            return [defaultMessage];
        } else {
            return this.getMessagesByStatusCode(statusCode, ignoreErrorDetails);
        }
    }

    private getErrorByCodeOrDefaultAsync(statusCode: number, defaultMessage?: string | null): Promise<ErrorMessage> {
        return new Promise((resolve, reject) => {
            if (defaultMessage) {
                resolve(new ErrorMessage(
                    defaultMessage,
                    null,
                ));
            } else {
                this.getMessagesByStatusCodeAsync(statusCode)
                    .then((errorMessageResult) => {
                        resolve(errorMessageResult);
                    }, (errorMessageResult) => {
                        reject(errorMessageResult);
                    });
            }
        });
    }

    private isErrorContract(error: any): error is IErrorContract {
        if (!error) {
            return false;
        }

        return (error as IErrorContract).errorDescriptions !== undefined;
    }

    private getMessagesByStatusCode(statusCode: number, ignoreErrorDetails: boolean = false): string[] {
        const messages: string[] = [];
        let errorMessage: string | undefined;
        let errorMessageDetail = "";
        // Warning: all server side projects (WebHub, Auth service, ...) must contain following localization:
        /* tslint:disable:deprecation */
        try {
            switch (statusCode) {
                case StatusCodes.Status400BadRequest:
                    errorMessage = this.mLocalization.translate("bad-request-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("bad-request-detail", "error").value;
                    break;
                case StatusCodes.Status401Unauthorized:
                    errorMessage = this.mLocalization.translate("unauthorized-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("unauthorized-detail", "error").value;
                    break;
                case StatusCodes.Status403Forbidden:
                    errorMessage = this.mLocalization.translate("forbidden-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("forbidden-detail", "error").value;
                    break;
                case StatusCodes.Status404NotFound:
                    errorMessage = this.mLocalization.translate("not-found-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("not-found-detail", "error").value;
                    break;
                case StatusCodes.Status500InternalServerError:
                    errorMessage = this.mLocalization.translate("internal-server-error-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("internal-server-error-detail", "error").value;
                    break;
                case StatusCodes.Status502BadGateway:
                    errorMessage = this.mLocalization.translate("bad-gateway-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("bad-gateway-detail", "error").value;
                    break;
                case StatusCodes.Status504GatewayTimeout:
                    errorMessage = this.mLocalization.translate("gateway-timeout-msg", "error").value;
                    errorMessageDetail = this.mLocalization.translate("gateway-timeout-detail", "error").value;
                    break;
                default:
                    errorMessage = this.mLocalization.translate("generic-error", "remote-error").value;
            }
        } catch (e) {
            // Fallback if failed to load localized error from server
            errorMessage = "";
        }
        /* tslint:enable:deprecation */

        if (errorMessage) {
            messages.push(errorMessage);
        }

        if (!ignoreErrorDetails && errorMessageDetail) {
            messages.push(errorMessageDetail);
        }

        return messages;
    }

    private getMessagesByStatusCodeAsync(statusCode: number): Promise<ErrorMessage> {
        return new Promise((resolve, reject) => {
            // Warning: all server side projects (WebHub, Auth service, ...) must contain following localization:
            this.mLocalization.getDictionaryAsync("error")
                .then((dictionaryResult) => {

                    let translationKey: string;
                    let translationDetailKey: string;

                    switch (statusCode) {
                        case StatusCodes.Status400BadRequest:
                            translationKey = "bad-request-msg";
                            translationDetailKey = "bad-request-detail";
                            break;
                        case StatusCodes.Status401Unauthorized:
                            translationKey = "unauthorized-msg";
                            translationDetailKey = "unauthorized-detail";
                            break;
                        case StatusCodes.Status403Forbidden:
                            translationKey = "forbidden-msg";
                            translationDetailKey = "forbidden-detail";
                            break;
                        case StatusCodes.Status404NotFound:
                            translationKey = "not-found-msg";
                            translationDetailKey = "not-found-detail";
                            break;
                        case StatusCodes.Status500InternalServerError:
                            translationKey = "internal-server-error-msg";
                            translationDetailKey = "internal-server-error-detail";
                            break;
                        case StatusCodes.Status502BadGateway:
                            translationKey = "bad-gateway-msg";
                            translationDetailKey = "bad-gateway-detail";
                            break;
                        case StatusCodes.Status504GatewayTimeout:
                            translationKey = "gateway-timeout-msg";
                            translationDetailKey = "gateway-timeout-detail";
                            break;
                        default:
                            this.mLocalization.translateAsync("generic-error", "remote-error")
                                .then((remoteErrorTranslation) => {
                                    resolve(new ErrorMessage(
                                        remoteErrorTranslation.result.value,
                                        null,
                                    ));
                                }, (remoteErrorTranslation) => {
                                    reject(new ErrorMessage(
                                        remoteErrorTranslation.result.value,
                                        null,
                                    ));
                                });

                            return;
                    }

                    if (dictionaryResult.result === null) {
                        throw new Error("failed to load error dictionary");
                    }

                    const errorMessage = dictionaryResult.result.translate(
                        translationKey,
                        () => {
                            if (dictionaryResult.result === null) {
                                throw new Error("failed to load error dictionary");
                            }

                            return dictionaryResult.result.getFallbackTranslation(
                                translationKey,
                                dictionaryResult.status.scope,
                                undefined as unknown as string,
                            );
                        },
                    )?.value;

                    const errorMessageDetail = dictionaryResult.result.translate(
                        translationDetailKey,
                        () => {
                            if (dictionaryResult.result === null) {
                                throw new Error("failed to load error dictionary");
                            }
                            return dictionaryResult.result.getFallbackTranslation(
                                translationDetailKey,
                                dictionaryResult.status.scope,
                                undefined as unknown as string,
                            );
                        },
                    )?.value;

                    if (errorMessage === undefined || errorMessageDetail === undefined) {
                        throw new Error("failed to localize error message");
                    }

                    resolve(new ErrorMessage(
                        errorMessage,
                        errorMessageDetail,
                    ));
                });
        });
    }
}
